/**
 * SetTableOptionsEngineFields — per-engine field renderer for
 * `SetTableOptionsFormBody` (DSE2 §3.E3).
 *
 * Per-engine surface:
 *   - mssql:    DATA_COMPRESSION + DURABILITY + SYSTEM_VERSIONING (with
 *               optional HISTORY_TABLE).
 *   - postgres: SET / RESET storage parameters (line-separated verbatim
 *               entries — `key = value` for SET, `key` for RESET).
 *   - mysql:    ENGINE / DEFAULT CHARSET / COLLATE / ROW_FORMAT /
 *               KEY_BLOCK_SIZE / STATS_PERSISTENT.
 *   - oracle:   PCTFREE / PCTUSED / INITRANS / MAXTRANS / STORAGE (opaque) /
 *               LOGGING|NOLOGGING / COMPRESS clause (opaque).
 *
 * Form-values shape + `buildTableOptionsChange` runtime helper live in the
 * sibling non-TSX module `setTableOptionsFormHelpers.ts` so this file only
 * exports React components (per the `react-refresh/only-export-components`
 * lint rule — see Slice C4 learnings).
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice E3.
 */
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/select';
import type { SqlEngine } from '../../../../sql-grammar/model';
import type {
  MssqlDataCompression,
  MssqlDurability,
  MysqlRowFormat,
  OracleLoggingMode,
} from '../../../../sql-resolver/types/ddlEdit';
import type {
  StatsPersistentMode,
  SystemVersioningMode,
  TableOptionsFormValues,
} from './setTableOptionsFormHelpers';

/** Radix Select forbids `value=""`; the "(unchanged)" option of every
 *  `SelectField` rides this sentinel through the widget and maps back to
 *  `''` on the way out, so `TableOptionsFormValues` keeps its `''` semantics. */
const UNSET = '__unset__' as const;

export interface SetTableOptionsEngineFieldsProps {
  readonly engine: SqlEngine;
  readonly testId: string;
  readonly values: TableOptionsFormValues;
  readonly onChange: (next: TableOptionsFormValues) => void;
}

export function SetTableOptionsEngineFields(props: SetTableOptionsEngineFieldsProps) {
  const { engine, testId, values, onChange } = props;
  const set = <K extends keyof TableOptionsFormValues>(key: K, value: TableOptionsFormValues[K]) => {
    onChange({ ...values, [key]: value });
  };

  if (engine === 'mssql') {
    return (
      <>
        <SelectField
          testId={`${testId}-compression`}
          label="DATA_COMPRESSION"
          value={values.mssqlCompression}
          onChange={(next) => set('mssqlCompression', next as '' | MssqlDataCompression)}
          options={[
            { value: '', label: '(unchanged)' },
            { value: 'none', label: 'NONE' },
            { value: 'row', label: 'ROW' },
            { value: 'page', label: 'PAGE' },
          ]}
          hint="Emitted via ALTER TABLE … REBUILD WITH (DATA_COMPRESSION = …)."
        />
        <SelectField
          testId={`${testId}-durability`}
          label="DURABILITY"
          value={values.mssqlDurability}
          onChange={(next) => set('mssqlDurability', next as '' | MssqlDurability)}
          options={[
            { value: '', label: '(unchanged)' },
            { value: 'schema-and-data', label: 'SCHEMA_AND_DATA' },
            { value: 'schema-only', label: 'SCHEMA_ONLY' },
          ]}
        />
        <SelectField
          testId={`${testId}-versioning`}
          label="SYSTEM_VERSIONING"
          value={values.mssqlVersioning}
          onChange={(next) => set('mssqlVersioning', next as SystemVersioningMode)}
          options={[
            { value: '', label: '(unchanged)' },
            { value: 'on', label: 'ON' },
            { value: 'off', label: 'OFF' },
          ]}
        />
        {values.mssqlVersioning === 'on' ? (
          <TextField
            testId={`${testId}-history-table`}
            label="HISTORY_TABLE (optional)"
            value={values.mssqlHistoryTable}
            onChange={(next) => set('mssqlHistoryTable', next)}
            placeholder="dbo.t_history"
          />
        ) : null}
      </>
    );
  }

  if (engine === 'postgres') {
    return (
      <>
        <TextareaField
          testId={`${testId}-pgsql-set`}
          label="SET (one entry per line)"
          value={values.pgsqlSetText}
          onChange={(next) => set('pgsqlSetText', next)}
          placeholder={'fillfactor = 70\nautovacuum_enabled = false'}
          rows={4}
          hint="Each line is a verbatim key = value entry — emitted as ALTER TABLE … SET (…)."
        />
        <TextareaField
          testId={`${testId}-pgsql-reset`}
          label="RESET (one key per line)"
          value={values.pgsqlResetText}
          onChange={(next) => set('pgsqlResetText', next)}
          placeholder={'fillfactor\nautovacuum_enabled'}
          rows={3}
        />
      </>
    );
  }

  if (engine === 'mysql') {
    return (
      <>
        <TextField
          testId={`${testId}-mysql-engine`}
          label="ENGINE"
          value={values.mysqlEngine}
          onChange={(next) => set('mysqlEngine', next)}
          placeholder="InnoDB"
        />
        <div className="grid grid-cols-2 gap-2">
          <TextField
            testId={`${testId}-mysql-charset`}
            label="DEFAULT CHARSET"
            value={values.mysqlCharset}
            onChange={(next) => set('mysqlCharset', next)}
            placeholder="utf8mb4"
          />
          <TextField
            testId={`${testId}-mysql-collate`}
            label="COLLATE"
            value={values.mysqlCollate}
            onChange={(next) => set('mysqlCollate', next)}
            placeholder="utf8mb4_unicode_ci"
          />
        </div>
        <SelectField
          testId={`${testId}-mysql-row-format`}
          label="ROW_FORMAT"
          value={values.mysqlRowFormat}
          onChange={(next) => set('mysqlRowFormat', next as '' | MysqlRowFormat)}
          options={[
            { value: '', label: '(unchanged)' },
            { value: 'compact', label: 'COMPACT' },
            { value: 'redundant', label: 'REDUNDANT' },
            { value: 'dynamic', label: 'DYNAMIC' },
            { value: 'compressed', label: 'COMPRESSED' },
          ]}
        />
        <div className="grid grid-cols-2 gap-2">
          <TextField
            testId={`${testId}-mysql-key-block-size`}
            label="KEY_BLOCK_SIZE"
            value={values.mysqlKeyBlockSize}
            onChange={(next) => set('mysqlKeyBlockSize', next)}
            placeholder="8"
            inputMode="numeric"
          />
          <SelectField
            testId={`${testId}-mysql-stats-persistent`}
            label="STATS_PERSISTENT"
            value={values.mysqlStatsPersistent}
            onChange={(next) => set('mysqlStatsPersistent', next as StatsPersistentMode)}
            options={[
              { value: '', label: '(unchanged)' },
              { value: '0', label: '0' },
              { value: '1', label: '1' },
              { value: 'default', label: 'DEFAULT' },
            ]}
          />
        </div>
      </>
    );
  }

  // oracle
  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        <TextField
          testId={`${testId}-oracle-pctfree`}
          label="PCTFREE"
          value={values.oraclePctfree}
          onChange={(next) => set('oraclePctfree', next)}
          inputMode="numeric"
        />
        <TextField
          testId={`${testId}-oracle-pctused`}
          label="PCTUSED"
          value={values.oraclePctused}
          onChange={(next) => set('oraclePctused', next)}
          inputMode="numeric"
        />
        <TextField
          testId={`${testId}-oracle-initrans`}
          label="INITRANS"
          value={values.oracleInitrans}
          onChange={(next) => set('oracleInitrans', next)}
          inputMode="numeric"
        />
        <TextField
          testId={`${testId}-oracle-maxtrans`}
          label="MAXTRANS"
          value={values.oracleMaxtrans}
          onChange={(next) => set('oracleMaxtrans', next)}
          inputMode="numeric"
        />
      </div>
      <TextField
        testId={`${testId}-oracle-storage`}
        label="STORAGE clause body"
        value={values.oracleStorage}
        onChange={(next) => set('oracleStorage', next)}
        placeholder="INITIAL 1M NEXT 1M"
        hint="Verbatim text inside STORAGE (…)."
      />
      <div className="grid grid-cols-2 gap-2">
        <SelectField
          testId={`${testId}-oracle-logging`}
          label="LOGGING"
          value={values.oracleLogging}
          onChange={(next) => set('oracleLogging', next as '' | OracleLoggingMode)}
          options={[
            { value: '', label: '(unchanged)' },
            { value: 'logging', label: 'LOGGING' },
            { value: 'nologging', label: 'NOLOGGING' },
          ]}
        />
        <TextField
          testId={`${testId}-oracle-compress`}
          label="COMPRESS clause"
          value={values.oracleCompress}
          onChange={(next) => set('oracleCompress', next)}
          placeholder="COMPRESS FOR OLTP"
        />
      </div>
    </>
  );
}

interface TextFieldProps {
  readonly testId: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly placeholder?: string;
  readonly inputMode?: 'numeric' | 'text';
  readonly hint?: string;
}

function TextField(props: TextFieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={props.testId} className="text-xs">{props.label}</Label>
      <Input
        id={props.testId}
        data-testid={props.testId}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder={props.placeholder}
        inputMode={props.inputMode}
        className="h-8 font-mono text-xs"
      />
      {props.hint ? <span className="text-[10px] text-muted-foreground">{props.hint}</span> : null}
    </div>
  );
}

interface TextareaFieldProps {
  readonly testId: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly placeholder?: string;
  readonly rows?: number;
  readonly hint?: string;
}

function TextareaField(props: TextareaFieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={props.testId} className="text-xs">{props.label}</Label>
      <textarea
        id={props.testId}
        data-testid={props.testId}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder={props.placeholder}
        rows={props.rows ?? 3}
        className="min-h-[64px] resize-y rounded-md border border-input bg-background px-2 py-1 font-mono text-xs"
      />
      {props.hint ? <span className="text-[10px] text-muted-foreground">{props.hint}</span> : null}
    </div>
  );
}

interface SelectFieldProps {
  readonly testId: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly options: ReadonlyArray<{ readonly value: string; readonly label: string }>;
  readonly hint?: string;
}

function SelectField(props: SelectFieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={props.testId} className="text-xs">{props.label}</Label>
      <Select
        value={props.value === '' ? UNSET : props.value}
        onValueChange={(v) => props.onChange(v === UNSET ? '' : v)}
      >
        <SelectTrigger
          id={props.testId}
          data-testid={props.testId}
          className="h-8 font-mono text-xs"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {props.options.map((o) => (
            <SelectItem key={o.value} value={o.value === '' ? UNSET : o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {props.hint ? <span className="text-[10px] text-muted-foreground">{props.hint}</span> : null}
    </div>
  );
}
